import { ChannelAttributes } from "@/app/service";
import { ViewerHoursUser } from "@/app/service/viewerHoursService";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import ReactCountryFlag from "react-country-flag";
import EmptyState from "../ui/EmptyState";
import SkeletonLoader from "../ui/SkeletonLoader";
import { EventCommentatorTile, GeneralCommentatorTile } from "./CommentatorTile";
import {Commentator} from "@/app/types";

// Shows current commentator's stats and viewer count
export const CurrentCommentatorStats = ({
    attributes,
    currentCommentatorId,
    displayName
}: {
    attributes: ChannelAttributes[];
    currentCommentatorId?: string;
    displayName: string;
}) => {
    const [previousViewerCount, setPreviousViewerCount] = useState<number | null>(null);
    const [previousRank, setPreviousRank] = useState<number | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const currentCommentator = attributes.find(attr => attr.groupId === currentCommentatorId);

    // Calculate current commentator's rank
    const allSorted = [...attributes].sort((a, b) => b.liveViewerCount - a.liveViewerCount);
    const currentRank = allSorted.findIndex(attr => attr.groupId === currentCommentatorId) + 1;

    // Toggle expanded state
    const toggleExpanded = () => {
        const newState = !isExpanded;
        setIsExpanded(newState);
    };

    // Track changes in viewer count and rank for visual feedback
    useEffect(() => {
        if (!currentCommentator) return;

        const currentViewerCount = currentCommentator.liveViewerCount;

        if (previousViewerCount !== null && 
            (previousViewerCount !== currentViewerCount || previousRank !== currentRank)) {
            setIsUpdating(true);
            // Clear the updating state after animation
            setTimeout(() => setIsUpdating(false), 1000);
        }

        setPreviousViewerCount(currentViewerCount);
        setPreviousRank(currentRank);
    }, [currentCommentator?.liveViewerCount, currentRank, previousViewerCount, previousRank]);

    if (!currentCommentator) {
        return (
            <div className="bg-white/80 rounded-lg border border-gray-200 shadow-md p-4">
                <div className="text-center text-gray-400 py-4">
                    Loading your stats...
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-r from-widertalk-navy to-widertalk-navy/90 rounded-lg border border-gray-200 shadow-md text-white">
            <div
                className="p-4 border-b border-white/20 cursor-pointer hover:bg-white/5 transition-colors duration-200"
                onClick={toggleExpanded}
            >
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-white text-sm font-semibold">Your Stats</h2>
                        <p className="text-white/70 text-xs">
                            {isExpanded
                                ? "There may be a short delay in viewer count figures"
                                : `${currentCommentator.liveViewerCount} viewers • Rank #${currentRank}`
                            }
                        </p>
                    </div>
                    <svg
                        className={`w-4 h-4 text-white transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            <div className={`
                transition-all duration-300 ease-in-out overflow-hidden
                ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
            `}>
                <div className="p-4">
                    <div className="flex items-center gap-4">
                        {/* Avatar with flag */}
                        <div className="relative flex-shrink-0">
                            <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-white/20">
                                <Image
                                    src={currentCommentator.user.avatarUrl || "/profile.svg"}
                                    alt={displayName}
                                    width={64}
                                    height={64}
                                    className="object-cover"
                                />
                            </div>
                            {currentCommentator.user.nationality && currentCommentator.user.nationality.trim() !== "" && (
                                <div className="absolute bottom-0 right-0 bg-white rounded-full p-0.5">
                                    <ReactCountryFlag
                                        countryCode={currentCommentator.user.nationality}
                                        svg
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%'
                                        }}
                                        aria-label={currentCommentator.user.nationality}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Stats */}
                        <div className="flex-1">
                            <div className="mb-2">
                                <div className="font-semibold text-lg text-white">
                                    {displayName}
                                </div>
                                {/* Co-commentators display */}
                                {currentCommentator.coCommentators && currentCommentator.coCommentators.length > 0 && (
                                    <div className="mt-2">
                                        <div className="text-xs text-white/70 mb-1">
                                            Team Commentary ({currentCommentator.coCommentators.length} co-host{currentCommentator.coCommentators.length > 1 ? 's' : ''}):
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {currentCommentator.coCommentators.map((coCommentator, index) => (
                                                <div key={`${coCommentator.publicHandle}-${index}`} className="bg-white/10 rounded-full px-3 py-1">
                                                    <span className="text-xs text-white font-medium">
                                                        {coCommentator.displayName}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                    <Image
                                        src="/viewers.svg"
                                        alt="Viewers"
                                        width={20}
                                        height={20}
                                        style={{ filter: 'invert(1)' }}
                                    />
                                    <div>
                                        <div className={`text-2xl font-bold text-white transition-all duration-500 ${isUpdating ? 'scale-110 text-green-300' : ''}`}>
                                            {currentCommentator.liveViewerCount}
                                        </div>
                                        <div className="text-xs text-white/70">viewers</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className={`
                                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500
                                        ${currentRank === 1
                                            ? 'bg-yellow-500 text-black'
                                            : currentRank === 2
                                                ? 'bg-gray-400 text-white'
                                                : currentRank === 3
                                                    ? 'bg-orange-600 text-white'
                                                    : 'bg-gray-600 text-white'
                                        }
                                        ${isUpdating ? 'scale-110 ring-2 ring-white/50' : ''}
                                    `}>
                                        {currentRank}
                                    </div>
                                    <div>
                                        <div className={`text-lg font-semibold text-white transition-all duration-500 ${isUpdating ? 'scale-110 text-green-300' : ''}`}>
                                            #{currentRank}
                                        </div>
                                        <div className="text-xs text-white/70">ranking</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Live indicator */}
                        <div className="flex items-center gap-2 bg-red-500 px-3 py-2 rounded-full">
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                            <span className="text-white text-sm font-medium">LIVE</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Shows top commentators (for home page, etc.)
export const TopCommentatorsList = ({
    onCommentatorClick
}: {
    onCommentatorClick?: (commentator: Commentator, rank: number) => void
}) => {
    const [loading, setLoading] = useState(true);
    const [topCommentators, setTopCommentators] = useState<ViewerHoursUser[]>([]);
    const [filteredCommentators, setFilteredCommentators] = useState<ViewerHoursUser[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSportCategory, setSelectedSportCategory] = useState<string>('FOOTBALL');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCommentators, setTotalCommentators] = useState(0);

    const itemsPerPage = 10;

    const fetchTopCommentators = async (page: number = 1, search: string = '', sportCategory: string = 'ALL') => {
        try {
            setLoading(true);
            setError(null);

            // Import the service dynamically to avoid potential circular imports
            const { ViewerHoursService } = await import('../../service/viewerHoursService');

            // Calculate offset for pagination
            const offset = (page - 1) * itemsPerPage;

            // Get commentators with pagination, search, and sport category filter
            const response = await ViewerHoursService.getViewerHoursRanked({
                limit: itemsPerPage,
                offset: offset,
                orderBy: 'rank',
                orderDirection: 'ASC',
                searchDisplayName: search.trim(),
                sportCategory: sportCategory === 'ALL' ? undefined : sportCategory
            });

            setTopCommentators(response.data.users);
            setFilteredCommentators(response.data.users);
            setTotalCommentators(response.data.total);
            setTotalPages(Math.ceil(response.data.total / itemsPerPage));
        } catch (err) {
            console.error('Error fetching top commentators:', err);
            setError('Failed to load top commentators');

            // Fallback: if request fails, try without filters
            if (search.trim() || sportCategory !== 'ALL') {
                console.log('Request failed, retrying without filters');
                try {
                    const { ViewerHoursService } = await import('../../service/viewerHoursService');
                    const response = await ViewerHoursService.getViewerHoursRanked({
                        limit: itemsPerPage,
                        offset: (page - 1) * itemsPerPage,
                        orderBy: 'rank',
                        orderDirection: 'ASC'
                    });
                    setTopCommentators(response.data.users);
                    setFilteredCommentators(response.data.users);
                    setTotalCommentators(response.data.total);
                    setTotalPages(Math.ceil(response.data.total / itemsPerPage));
                    setError('Filters temporarily unavailable - showing all results');
                } catch (fallbackErr) {
                    console.error('Fallback also failed:', fallbackErr);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTopCommentators(currentPage, searchTerm, selectedSportCategory);
    }, [currentPage]);

    // Handle search and sport category filter with debouncing
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            // Reset to page 1 when searching or filtering
            setCurrentPage(1);
            fetchTopCommentators(1, searchTerm, selectedSportCategory);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, selectedSportCategory]);

    const handleCommentatorClick = (commentator: Commentator, rank: number) => {
        if (onCommentatorClick) {
            onCommentatorClick(commentator, rank);
        }
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        // Don't clear search when changing pages - maintain search context
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    if (loading) {
        return (
            <div className="flex h-full flex-col justify-between gap-4 pb-8">
                <div className="flex flex-col justify-start gap-4">
                    <SkeletonLoader type="commentatorTile" count={3} />
                </div>
                <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
            </div>
        );
    }

    const displayedCommentators = filteredCommentators;
    const startRank = (currentPage - 1) * itemsPerPage + 1;

    return (
        <div className="flex h-full flex-col gap-4 pb-8">
            {/* Sport Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {['FOOTBALL', 'CYCLING'].map((category) => (
                    <button
                        key={category}
                        onClick={() => setSelectedSportCategory(category)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                            selectedSportCategory === category
                                ? 'bg-widertalk-navy text-white shadow-md'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        {category.charAt(0) + category.slice(1).toLowerCase()}
                    </button>
                ))}
            </div>

            {/* Search Bar */}
            <div className="relative">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search commentators..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="w-full px-4 py-3 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-widertalk-navy focus:border-transparent transition-colors bg-white/90"
                    />
                    {/* Search Icon */}
                    <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>

                    {/* Clear Button */}
                    {searchTerm && (
                        <button
                            onClick={clearSearch}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="Clear search"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Search Results Info */}
                {(searchTerm || selectedSportCategory !== 'ALL') && (
                    <div className="mt-2 text-sm text-gray-600">
                        {displayedCommentators.length === 0 ? (
                            <span>No commentators found</span>
                        ) : (
                            <span>
                                Found {totalCommentators} commentator{totalCommentators !== 1 ? 's' : ''}
                                {selectedSportCategory !== 'ALL' && ` in ${selectedSportCategory.toLowerCase()}`}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Commentators List */}
            <div className="flex-1 flex flex-col gap-4">
                {displayedCommentators.length > 0 ? (
                    displayedCommentators.map((commentator, index) => {
                        // Convert ViewerHoursUser to format expected by GeneralCommentatorTile
                        const commentatorData: Commentator = {
                            publicHandle: commentator.publicHandle,
                            displayName: commentator.displayName,
                            avatarUrl: commentator.avatarUrl,
                            nationality: commentator.nationality,
                            preferredLanguage: commentator.preferredLanguage,
                            joinedTimestamp: commentator.joinedTimestamp,
                            description: commentator.description,
                            totalViewerHours: commentator.totalViewerHours,
                            avgViewerCount: commentator.avgViewerCount,
                            rank: commentator.rank,
                        };

                        const getSportCategoryBadgeColor = (sport: string) => {
                            switch (sport) {
                                case 'FOOTBALL': return 'bg-green-500';
                                case 'CYCLING': return 'bg-blue-500';
                                default: return 'bg-gray-500';
                            }
                        };

                        return (
                            <div key={`${commentator.publicHandle}-${commentator.sportCategory}`} className="relative">
                                {/* Ranking badge */}
                                <div className={`
                                    absolute -top-1 -left-1 z-10 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                                    ${commentator.rank === 1
                                        ? 'bg-yellow-500 text-black'
                                        : commentator.rank === 2
                                            ? 'bg-gray-400 text-white'
                                            : commentator.rank === 3
                                                ? 'bg-orange-600 text-white'
                                                : 'bg-gray-600 text-white'
                                    }
                                `}>
                                    {commentator.rank}
                                </div>

                                {/* Sport category badge */}
                                <div className={`
                                    absolute -top-1 -right-1 z-10 ${getSportCategoryBadgeColor(commentator.sportCategory)}
                                    text-white text-xs font-semibold px-2 py-0.5 rounded-full shadow-md
                                `}>
                                    {commentator.sportCategory.charAt(0) + commentator.sportCategory.slice(1).toLowerCase()}
                                </div>

                                <GeneralCommentatorTile
                                    groupId={commentator.publicHandle}
                                    commentatorData={commentatorData}
                                    rank={commentator.rank}
                                    onClick={() => handleCommentatorClick(commentatorData, commentator.rank)}
                                />
                            </div>
                        );
                    })
                ) : (
                    searchTerm && (
                        <div className="text-center py-8 text-gray-500">
                            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <p className="text-lg font-medium mb-2">No commentators found</p>
                            <p className="text-sm">Try adjusting your search terms</p>
                        </div>
                    )
                )}
            </div>

            {/* Error Banner */}
            {error && (
                <div className="text-xs text-yellow-600 bg-yellow-50 px-3 py-2 rounded-lg border border-yellow-200">
                    ⚠️ Using cached data - API temporarily unavailable
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-4 gap-4">
                    {/* Page Info */}
                    <div className="text-sm text-gray-600 order-2 sm:order-1">
                        Page {currentPage} of {totalPages}
                    </div>

                    {/* Simplified Pagination Buttons */}
                    <div className="flex items-center gap-3 order-1 sm:order-2">
                        {/* Previous Button */}
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`
                                px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
                                ${currentPage === 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-widertalk-navy text-white hover:bg-widertalk-navy/90'
                                }
                            `}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Previous
                        </button>

                        {/* Next Button */}
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`
                                px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
                                ${currentPage === totalPages
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-widertalk-navy text-white hover:bg-widertalk-navy/90'
                                }
                            `}
                        >
                            Next
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Shows commentators for a specific event with animations
export const EventCommentatorsList = ({
    attributes,
    onCommentator,
    setOnCommentator
}: {
    attributes: ChannelAttributes[];
    onCommentator: string | null;
    setOnCommentator: (key: string | null) => void;
}) => {
    const [sortedAttributes, setSortedAttributes] = useState<ChannelAttributes[]>([]);
    const [animatingItems, setAnimatingItems] = useState<Set<string>>(new Set());
    const previousOrderRef = useRef<string[]>([]);

    // Sort attributes by liveViewerCount (descending)
    const sortByViewerCount = (attrs: ChannelAttributes[]) => {
        return [...attrs].sort((a, b) => b.liveViewerCount - a.liveViewerCount);
    };

    // Initialize sorted attributes and handle order changes for animation
    useEffect(() => {
        if (attributes && attributes.length > 0) {
            const sorted = sortByViewerCount(attributes);
            const newOrder = sorted.map(attr => attr.groupId);
            const oldOrder = previousOrderRef.current;

            // Check if order has changed for animation
            if (oldOrder.length > 0) {
                const orderChanged = newOrder.some((id, index) => id !== oldOrder[index]);

                if (orderChanged) {
                    // Mark items that changed position for animation
                    const changedItems = new Set<string>();
                    newOrder.forEach((id, newIndex) => {
                        const oldIndex = oldOrder.indexOf(id);
                        if (oldIndex !== newIndex && oldIndex !== -1) {
                            changedItems.add(id);
                        }
                    });

                    setAnimatingItems(changedItems);

                    // Clear animation after transition completes
                    setTimeout(() => {
                        setAnimatingItems(new Set());
                    }, 500);
                }
            }

            setSortedAttributes(sorted);
            previousOrderRef.current = newOrder;
        }
    }, [attributes]);

    if (!sortedAttributes || !sortedAttributes.length) {
        return (
            <div className="text-center text-gray-400 py-8">
                No commentators available for this event.
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 relative max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
            {sortedAttributes.map((attribute: ChannelAttributes, index: number) => {
                const isAnimating = animatingItems.has(attribute.groupId);

                return (
                    <div
                        key={attribute.groupId}
                        className={`
                            transition-all duration-500 ease-in-out
                            ${isAnimating ? 'transform scale-105 z-10' : 'transform scale-100 z-0'}
                        `}
                        style={{
                            transform: isAnimating
                                ? `translateY(${Math.sin(Date.now() / 200) * 2}px) scale(1.02)`
                                : 'translateY(0px) scale(1)',
                        }}
                    >
                        <div className={`
                            transition-all duration-500 ease-in-out
                            ${isAnimating ? 'shadow-lg shadow-purple-500/20 ring-2 ring-purple-400/30' : ''}
                        `}>
                            <EventCommentatorTile
                                attributes={attribute}
                                onCommentator={onCommentator}
                                setOnCommentator={setOnCommentator}
                            />
                        </div>

                        {/* Ranking indicator */}
                        <div className={`
                            absolute left-1 top-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                            transition-all duration-300
                            ${index === 0
                                ? 'bg-yellow-500 text-black'
                                : index === 1
                                    ? 'bg-gray-400 text-white'
                                    : index === 2
                                        ? 'bg-orange-600 text-white'
                                        : 'bg-gray-600 text-white'
                            }
                            ${isAnimating ? 'animate-pulse scale-110' : ''}
                        `}>
                            {index + 1}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Shows commentators for viewing only (no interaction) - used on commentate page
export const CommentatorViewOnlyList = ({
    attributes,
    currentCommentatorId
}: {
    attributes: ChannelAttributes[];
    currentCommentatorId?: string;
}) => {
    const [sortedAttributes, setSortedAttributes] = useState<ChannelAttributes[]>([]);
    const [animatingItems, setAnimatingItems] = useState<Set<string>>(new Set());
    const [expandedCoCommentators, setExpandedCoCommentators] = useState<Set<string>>(new Set());
    const [isExpanded, setIsExpanded] = useState(false);
    const previousOrderRef = useRef<string[]>([]);

    // Sort attributes by liveViewerCount (descending) - includes current commentator
    const sortByViewerCount = (attrs: ChannelAttributes[]) => {
        return [...attrs].sort((a, b) => b.liveViewerCount - a.liveViewerCount);
    };

    // Toggle expanded state
    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Initialize sorted attributes with animation tracking
    useEffect(() => {
        if (attributes && attributes.length > 0) {
            const sorted = sortByViewerCount(attributes);
            const newOrder = sorted.map(attr => attr.groupId);
            const oldOrder = previousOrderRef.current;

            // Check if order has changed for animation
            if (oldOrder.length > 0) {
                const orderChanged = newOrder.some((id, index) => id !== oldOrder[index]);

                if (orderChanged) {
                    // Mark items that changed position for animation
                    const changedItems = new Set<string>();
                    newOrder.forEach((id, newIndex) => {
                        const oldIndex = oldOrder.indexOf(id);
                        if (oldIndex !== newIndex && oldIndex !== -1) {
                            changedItems.add(id);
                        }
                    });

                    setAnimatingItems(changedItems);

                    // Clear animation after transition completes
                    setTimeout(() => {
                        setAnimatingItems(new Set());
                    }, 1000);
                }
            }

            setSortedAttributes(sorted);
            previousOrderRef.current = newOrder;
        }
    }, [attributes, currentCommentatorId]);

    const toggleCoCommentators = (groupId: string) => {
        setExpandedCoCommentators(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    if (!sortedAttributes || !sortedAttributes.length) {
        return (
            <div className="bg-white/80 rounded-lg border border-gray-200 shadow-md flex-shrink-0">
                <div
                    className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50/50 transition-colors duration-200"
                    onClick={toggleExpanded}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-widertalk-navy text-sm font-semibold">Commentators</h2>
                            <p className="text-gray-500 text-xs">No commentators available</p>
                        </div>
                        <svg
                            className={`w-4 h-4 text-widertalk-navy transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
                <div className={`
                    transition-all duration-300 ease-in-out overflow-hidden
                    ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                `}>
                    <div className="p-4">
                        <div className="text-center text-gray-400 py-8">
                            No commentators available for this event.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white/80 rounded-lg border border-gray-200 shadow-md flex-shrink-0">
            <div
                className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50/50 transition-colors duration-200"
                onClick={toggleExpanded}
            >
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-widertalk-navy text-sm font-semibold">Commentators</h2>
                        <p className="text-gray-500 text-xs">
                            {isExpanded
                                ? "Ranked by viewer count"
                                : `${sortedAttributes.length} commentator${sortedAttributes.length !== 1 ? 's' : ''} available`
                            }
                        </p>
                    </div>
                    <svg
                        className={`w-4 h-4 text-widertalk-navy transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            <div className={`
                transition-all duration-300 ease-in-out overflow-hidden
                ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
            `}>
                <div className="p-4 max-h-80 overflow-y-auto">
                    <div className="flex flex-col gap-3">
                        {sortedAttributes.map((attribute: ChannelAttributes, index: number) => {
                            const isAnimating = animatingItems.has(attribute.groupId);
                            const isCurrentCommentator = attribute.groupId === currentCommentatorId;
                            const hasCoCommentators = attribute.coCommentators && attribute.coCommentators.length > 0;
                            const isExpanded = expandedCoCommentators.has(attribute.groupId);

                            return (
                            <div key={attribute.groupId} className="relative">
                                <div
                                    className={`
                                        relative w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-500 ease-in-out
                                        ${hasCoCommentators ? 'ring-2 ring-widertalk-navy/30' : ''}
                                        ${isCurrentCommentator 
                                            ? 'bg-widertalk-navy/10 border-2 border-widertalk-navy' 
                                            : 'bg-gray-50 border border-gray-200'
                                        }
                                        ${isAnimating ? 'scale-105 shadow-lg border-widertalk-navy bg-widertalk-lime/20' : ''}
                                    `}
                                >
                                    {/* Co-commentators indicator badge - top left */}
                                    {hasCoCommentators && (
                                        <div className="absolute -top-1 -left-1 z-10">
                                            <div className="bg-widertalk-navy text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                                                </svg>
                                                +{attribute.coCommentators.length}
                                            </div>
                                        </div>
                                    )}

                                    {/* Avatar with flag */}
                                    <div className="relative flex-shrink-0">
                                        <div className={`w-12 h-12 rounded-lg overflow-hidden border-2 ${hasCoCommentators ? 'border-widertalk-navy' : 'border-gray-300'}`}>
                                            <Image
                                                src={attribute.user.avatarUrl || "/profile.svg"}
                                                alt={attribute.user.displayName}
                                                width={48}
                                                height={48}
                                                className="object-cover"
                                            />
                                        </div>
                                        {attribute.user.nationality && attribute.user.nationality.trim() !== "" && (
                                            <div className="absolute bottom-0 right-0 bg-white rounded-full p-0.5">
                                                <ReactCountryFlag
                                                    countryCode={attribute.user.nationality}
                                                    svg
                                                    style={{
                                                        width: '14px',
                                                        height: '14px',
                                                        borderRadius: '50%'
                                                    }}
                                                    aria-label={attribute.user.nationality}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 text-left">
                                        <div className="font-medium text-sm text-widertalk-navy">
                                            {attribute.user.displayName}
                                            {hasCoCommentators && (
                                                <span className="ml-2 text-xs text-widertalk-navy font-semibold">
                                                    • Team Commentary
                                                </span>
                                            )}
                                        </div>
                                        {/* Stats */}
                                        <div className="flex items-center gap-3 mt-1">
                                            <div className="flex items-center gap-1">
                                                <Image
                                                    src="/viewers.svg"
                                                    alt="Viewers"
                                                    width={14}
                                                    height={14}
                                                    style={{ filter: 'invert(0.2)' }}
                                                />
                                                <span className={`text-xs text-widertalk-navy transition-all duration-500 ${isAnimating ? 'font-bold text-widertalk-navy scale-110' : ''}`}>
                                                    {attribute.liveViewerCount}
                                                </span>
                                            </div>
                                            {/* Co-commentators expandable button */}
                                            {hasCoCommentators && (
                                                <button
                                                    onClick={() => toggleCoCommentators(attribute.groupId)}
                                                    className={`flex items-center gap-1 px-3 py-1 rounded-full transition-all duration-200 ${
                                                        isExpanded 
                                                            ? 'bg-widertalk-navy text-white shadow-md scale-105' 
                                                            : 'bg-widertalk-navy/20 text-widertalk-navy hover:bg-widertalk-navy/30'
                                                    }`}
                                                    title={`View ${attribute.coCommentators.length} co-commentator${attribute.coCommentators.length > 1 ? 's' : ''}`}
                                                >
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span className="text-xs font-semibold">
                                                        {attribute.coCommentators.length} Co-host{attribute.coCommentators.length > 1 ? 's' : ''}
                                                    </span>
                                                    <svg
                                                        className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Ranking indicator */}
                                    <div className={`
                                        w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500
                                        ${index === 0
                                            ? 'bg-yellow-500 text-black'
                                            : index === 1
                                                ? 'bg-gray-400 text-white'
                                                : index === 2
                                                    ? 'bg-orange-600 text-white'
                                                    : 'bg-gray-600 text-white'
                                        }
                                        ${isAnimating ? 'scale-125 ring-2 ring-widertalk-navy/50' : ''}
                                    `}>
                                        {index + 1}
                                    </div>
                                </div>

                                {/* Co-commentators expandable section */}
                                {hasCoCommentators && (
                                    <div className={`
                                        overflow-hidden transition-all duration-300 ease-in-out
                                        ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                                    `}>
                                        <div className="bg-gradient-to-r from-widertalk-navy/10 to-widertalk-lime/10 border-l-4 border-widertalk-navy rounded-b-lg p-4 -mt-1 shadow-inner">
                                            <div className="flex items-center gap-2 mb-3">
                                                <svg className="w-4 h-4 text-widertalk-navy" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                                                </svg>
                                                <span className="text-sm font-bold text-widertalk-navy">
                                                    Team Commentary ({attribute.coCommentators.length} co-host{attribute.coCommentators.length > 1 ? 's' : ''})
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-1 gap-3">
                                                {attribute.coCommentators.map((coCommentator, coIndex) => (
                                                    <div key={`${coCommentator.publicHandle}-${coIndex}`} className="flex items-center gap-3 bg-white/60 rounded-lg p-3 shadow-sm">
                                                        <div className="relative flex-shrink-0">
                                                            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-widertalk-navy/30">
                                                                <Image
                                                                    src={coCommentator.avatarUrl || "/profile.svg"}
                                                                    alt={coCommentator.displayName}
                                                                    width={40}
                                                                    height={40}
                                                                    className="object-cover"
                                                                />
                                                            </div>
                                                            {coCommentator.nationality && coCommentator.nationality.trim() !== "" && (
                                                                <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
                                                                    <ReactCountryFlag
                                                                        countryCode={coCommentator.nationality}
                                                                        svg
                                                                        style={{
                                                                            width: '12px',
                                                                            height: '12px',
                                                                            borderRadius: '50%'
                                                                        }}
                                                                        aria-label={coCommentator.nationality}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm font-semibold text-widertalk-navy truncate">
                                                                {coCommentator.displayName}
                                                            </div>
                                                            <div className="text-xs text-gray-600 truncate">
                                                                @{coCommentator.publicHandle}
                                                            </div>
                                                        </div>
                                                        <div className="flex-shrink-0">
                                                            <div className="bg-widertalk-navy/20 text-widertalk-navy px-2 py-1 rounded-full text-xs font-medium">
                                                                Co-host
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
